/**
 * BF2142 Map- & Spielmodus-Konstanten — 1:1 portiert aus der Legacy-App
 * (`legacy-php/bf2142admin/assets/app.js`: `GAMETYPES` + `FALLBACK_MAPS`).
 * Echte Daten, keine Erfindungen. Labels kommen aus dem bestehenden
 * `formatMapName` (parse.ts), damit es keine doppelte Label-Quelle gibt.
 *
 * Sizes sind PRO MODUS (nicht pro Map) — wie im Original; der Gameserver
 * validiert die finale Kombination. Map-Slugs sind lowercase (so erwartet es
 * `mapList.append <map> <mode> <size>`).
 */

import { formatMapName } from "../ui/parse";

/** Echte BF2142-Maps (Slugs, lowercase) — Legacy `FALLBACK_MAPS`. */
export const BF2142_MAPS: readonly string[] = [
  "belgrade",
  "bridge_at_remagen",
  "camp_gibraltar",
  "cerbere_landing",
  "fall_of_berlin",
  "highway_tampa",
  "liberation_of_leipzig",
  "minsk",
  "molokai",
  "operation_blue_pearl",
  "operation_shingle",
  "port_bavaria",
  "shuhia_taiba",
  "sidi_power_plant",
  "strike_at_karkand",
  "suez_canal",
  "tunis_harbor",
  "verdun",
  "wake_island_2142",
  "yellow_knife",
] as const;

export interface GameMode {
  id: string;
  name: string;
  sizes: number[];
  def: number;
}

/** Spielmodi mit verfügbaren Größen + Default — Legacy `GAMETYPES`. */
export const GAME_MODES: readonly GameMode[] = [
  { id: "gpm_cq", name: "Conquest", sizes: [16, 32, 64], def: 64 },
  { id: "gpm_ti", name: "Titan", sizes: [16, 32, 64], def: 64 },
  { id: "gpm_ca", name: "Conquest Assault", sizes: [16, 32, 64], def: 64 },
  { id: "gpm_sl", name: "Assault Lines", sizes: [16, 24, 32, 64], def: 64 },
  { id: "gpm_nv", name: "No Vehicles", sizes: [16, 32, 64], def: 64 },
  { id: "gpm_coop", name: "Coop", sizes: [16, 32, 64], def: 16 },
] as const;

/** Map-Slug → `{ slug, label }` (Label via formatMapName). */
export function mapOptions(slugs: readonly string[] = BF2142_MAPS): { slug: string; label: string }[] {
  return slugs.map((s) => ({ slug: s, label: formatMapName(s) }));
}

/** Modus-Definition zu einer id (Fallback = erster Modus). */
export function gameMode(id: string): GameMode {
  return GAME_MODES.find((g) => g.id === id) ?? GAME_MODES[0]!;
}

/** Verfügbare Größen für einen Modus. */
export function modeSizes(id: string): number[] {
  return gameMode(id).sizes;
}

/** Default-Größe für einen Modus. */
export function defaultSize(id: string): number {
  return gameMode(id).def;
}

/** Größe → Label wie Legacy (`"16 players"`). */
export function sizeLabel(n: number | string): string {
  return `${n} players`;
}

/**
 * Erkennt RCON-Methodenfehler-Antworten, bei denen der Server `mapList.listAll`
 * NICHT unterstützt (Server-/RCON-Eigenheit, z.B. afex-suez-gunship:
 * `Unknown object or method!`). Auch leere Antworten zählen als „kein Ergebnis".
 * Map-Slugs (`[a-z0-9_]+`) enthalten keine dieser Wörter/Satzzeichen, daher
 * verwirft das niemals eine echte Mapliste — nur Fehlertext.
 */
export function isRconMethodError(raw: string | null | undefined): boolean {
  const s = String(raw ?? "").trim();
  if (!s) return true;
  // „Unknown object or method!", „Unknown command", „Invalid command",
  // „... not found", „Error: ...", „unsupported" usw.
  return /\b(unknown|invalid|unsupported|error)\b/i.test(s)
    || /\bnot\s+found\b/i.test(s)
    || /method/i.test(s);
}

/**
 * Live-Mapliste aus `mapList.listAll` parsen (Legacy `ensureAddForm`):
 * whitespace-getrennt, Quotes weg, dedupliziert + sortiert. Liefert `null`
 * (→ Fallback `BF2142_MAPS`), wenn die Antwort ein RCON-Methodenfehler/leer ist
 * ODER kein einziges Token wie ein echter Map-Slug aussieht. So landet ein roher
 * RCON-Fehlertext NIE als Map-Option im Dropdown (Defense-in-Depth: erst die
 * Methodenfehler-Erkennung, dann ein strikter Slug-Filter `^[a-z0-9][a-z0-9_]*$`).
 */
export function parseAvailableMaps(raw: string | null | undefined): string[] | null {
  const s = String(raw ?? "").trim();
  if (isRconMethodError(s)) return null;
  const maps = s
    .split(/\s+/)
    .map((m) => m.trim().replace(/^"|"$/g, "").toLowerCase())
    .filter((m) => /^[a-z0-9][a-z0-9_]*$/.test(m));
  return maps.length ? [...new Set(maps)].sort() : null;
}
